import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { X, Boat, Buildings } from "@phosphor-icons/react";

/**
 * StartExportModal — Choose an upcoming GTRB vessel + starting silo,
 * then call POST /tronox/exports/start.
 */
export default function StartExportModal({ onClose, onSuccess }) {
    const [vessels, setVessels] = useState([]);
    const [silos, setSilos] = useState([]);
    const [lineupId, setLineupId] = useState("");
    const [siloNo, setSiloNo] = useState("");
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    const selectedVessel = vessels.find((v) => v.id === lineupId);
    const vesselProduct = (selectedVessel?.product || "").trim().toLowerCase();

    useEffect(() => {
        (async () => {
            try {
                const [vRes, sRes] = await Promise.all([
                    api.get("/tronox/upcoming-vessels"),
                    api.get("/tronox/silos"),
                ]);
                const upcoming = (vRes.data || []).filter((v) => v._state === "upcoming");
                setVessels(upcoming);
                setSilos(sRes.data?.silos || []);
                if (upcoming.length > 0) setLineupId(upcoming[0].id);
            } catch (e) { toast.error(formatApiError(e)); }
            setLoading(false);
        })();
    }, []);

    // Reset silo choice when vessel changes
    useEffect(() => { setSiloNo(""); }, [lineupId]);

    const submit = async (e) => {
        e.preventDefault();
        if (!lineupId) return toast.error("Select a vessel from the GTRB lineup.");
        if (!siloNo) return toast.error("Select the silo to load from.");
        setBusy(true);
        try {
            await api.post("/tronox/exports/start", { lineup_id: lineupId, silo_no: Number(siloNo) });
            toast.success("Export operation started.");
            onSuccess();
        } catch (e2) { toast.error(formatApiError(e2)); }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-3" data-testid="start-export-modal">
            <div className="bg-slate-900 text-slate-100 rounded-xl w-full max-w-lg border border-slate-700 shadow-2xl">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
                    <div>
                        <div className="text-[11px] uppercase tracking-widest text-slate-400">GTRB Outbound</div>
                        <div className="text-lg font-black">Start vessel export</div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-white p-1" data-testid="close-start-export">
                        <X size={20} />
                    </button>
                </div>
                <form onSubmit={submit} className="p-4 space-y-4">
                    {loading ? (
                        <div className="text-center text-slate-400 py-8">Loading GTRB lineup…</div>
                    ) : vessels.length === 0 ? (
                        <div className="text-center text-slate-400 py-8">
                            <div className="text-lg font-bold mb-1">No upcoming GTRB vessels</div>
                            <div className="text-xs">Add a vessel to the lineup with division = GTRB to start an export.</div>
                        </div>
                    ) : (
                        <>
                            <div>
                                <label className="text-[11px] uppercase text-slate-400 tracking-wider">Vessel (GTRB lineup)</label>
                                <select
                                    value={lineupId}
                                    onChange={(e) => setLineupId(e.target.value)}
                                    className="g-input mt-1"
                                    data-testid="start-export-vessel-select"
                                >
                                    {vessels.map((v) => (
                                        <option key={v.id} value={v.id}>
                                            {v.vessel_name} · {v.client_name} · {v.product} · ETA {v.eta ? new Date(v.eta).toLocaleString() : new Date(v.start_datetime).toLocaleString()}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="text-[11px] uppercase text-slate-400 tracking-wider">Starting silo</label>
                                <select
                                    value={siloNo}
                                    onChange={(e) => setSiloNo(e.target.value)}
                                    className="g-input mt-1"
                                    data-testid="start-export-silo-select"
                                >
                                    <option value="">— Choose a silo —</option>
                                    {silos.map((s) => {
                                        const empty = s.current_level_tons <= 0;
                                        const productMismatch = !empty && vesselProduct && (s.product || "").trim().toLowerCase() !== vesselProduct;
                                        const disabled = empty || productMismatch;
                                        const tag = empty ? " (empty)" : productMismatch ? ` (product mismatch — ${s.product})` : "";
                                        return (
                                            <option key={s.silo_no} value={s.silo_no} disabled={disabled}>
                                                {`Silo ${s.silo_no} · ${s.product} · ${Math.round(s.current_level_tons)} t on hand${tag}`}
                                            </option>
                                        );
                                    })}
                                </select>
                                <div className="text-[10px] text-slate-500 mt-1">
                                    Only silos matching the vessel product ({selectedVessel?.product || "—"}) are enabled. You can change silo mid-load.
                                </div>
                            </div>
                            <div className="flex gap-2 pt-2">
                                <button type="button" onClick={onClose} className="flex-1 py-2 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold" data-testid="start-export-cancel">Cancel</button>
                                <button type="submit" disabled={busy} className="flex-1 inline-flex items-center justify-center gap-2 py-2 rounded bg-sky-600 hover:bg-sky-500 disabled:bg-slate-700 text-white font-black" data-testid="start-export-submit">
                                    <Boat size={16} weight="bold" /> {busy ? "Starting…" : "Start export"}
                                </button>
                            </div>
                        </>
                    )}
                </form>
            </div>
        </div>
    );
}
